import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import { AuthPageLayout, AuthStatusBadge } from "../auth/AuthPageLayout";
import {
  makeAuthColors,
  makeGlassButtonSx,
  primaryButtonSx,
} from "../auth/authStyles";

const VerifyEmailPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { textSecondary, greenLink } = makeAuthColors(isDark);
  const glassButtonSx = makeGlassButtonSx(isDark);

  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  useEffect(() => {
    const verifyEmail = async () => {
      // Check if there's an error in the URL (Better Auth redirects with error)
      const error = searchParams.get("error");
      const token = searchParams.get("token");

      if (error) {
        setIsVerifying(false);
        if (error === "invalid_token") {
          setVerificationError("This verification link is invalid or has expired. Please request a new one.");
        } else {
          setVerificationError("An error occurred during verification. Please try again.");
        }
        return;
      }

      if (!token) {
        setIsVerifying(false);
        setVerificationError("No verification token provided. Please check your email for the verification link.");
        return;
      }

      // Better Auth automatically handles the verification via the URL
      // The token is processed by Better Auth's backend
      // We just need to wait a moment and then check the result
      // The redirect will happen automatically with success or error

      // If we reach here and there's no error, verification was successful
      setTimeout(() => {
        setIsVerifying(false);
        setVerificationSuccess(true);
      }, 1500); // Small delay for better UX
    };

    verifyEmail();
  }, [searchParams]);

  // No toast on arrival — the user just saw the full-screen confirmation.
  const continueToSignIn = () => {
    navigate("/sign-in?method=email");
  };

  if (isVerifying) {
    return (
      <AuthPageLayout
        contentKey="verifying"
        topSlot={
          <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "center" }, mb: 2.5 }}>
            <CircularProgress size={56} sx={{ color: "#22c55e" }} />
          </Box>
        }
        headline="Verifying your email"
        supporting="One moment while we confirm your address."
      />
    );
  }

  if (verificationSuccess) {
    return (
      <AuthPageLayout
        contentKey="success"
        topSlot={
          <AuthStatusBadge tone="success">
            <CheckRounded sx={{ fontSize: 36 }} />
          </AuthStatusBadge>
        }
        headline="Email verified"
        supporting="You're all set. Sign in and pick up where you left off."
      >
        <Button fullWidth onClick={continueToSignIn} sx={primaryButtonSx}>
          Continue to sign in
        </Button>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      contentKey="error"
      topSlot={
        <AuthStatusBadge tone="error">
          <ErrorOutlineRounded sx={{ fontSize: 34 }} />
        </AuthStatusBadge>
      }
      headline="Verification failed"
      supporting={verificationError}
    >
      <Button fullWidth onClick={() => navigate("/sign-in")} sx={glassButtonSx}>
        Back to sign in
      </Button>
      <Typography
        sx={{
          mt: 2.5,
          fontSize: "0.875rem",
          color: textSecondary,
          textAlign: { xs: "left", sm: "center" },
        }}
      >
        Need help?{" "}
        <Link
          href="mailto:support@insightanalytics.io"
          sx={{ color: greenLink, fontWeight: 600, textDecoration: "none" }}
        >
          Contact support
        </Link>
      </Typography>
    </AuthPageLayout>
  );
};

export default VerifyEmailPage;
